import React from 'react';
import List from '@material-ui/core/List';
import Avatar from '@material-ui/core/Avatar';
import SwipeableDrawer from '@material-ui/core/SwipeableDrawer';
import { Divider, Grid, Icon, IconButton, Typography } from '@material-ui/core';
import { makeStyles, Theme, createStyles } from '@material-ui/core/styles';
import { ListItem, ListItemAvatar, ListItemSecondaryAction } from '@material-ui/core';

import { HomeContext } from '../../Home/HomeContext';
import { PageLocation } from '../../../shared/enum/PageLocation';
import { AuthContext } from '../../Authentication/AuthContext';
import { MenuItems } from '../enums/MenuItems';
import MenuItemComponent from './MenuItemComponent';
import MenuInfluencerICE from './InfluencerMenus/MenuInfluencerICE';
import MenuInfluencer from './InfluencerMenus/MenuInfluencer';
import DashboardService, { IDashboard } from '../../../Services/DashboardService';

interface ISwipeableMenuProps {
    open: boolean,
    setOpen: (open: boolean) => void
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            width: '100%',
            maxWidth: 360,
            backgroundColor: '#000000',
            minHeight: '100vh',
            height: '100vh',
            overflow: 'hidden',
        },
        list: {
            width: 250,
            color: '#FFFFFF',
        },
        buttonMenu: {
            marginRight: 0,
            color: 'white',
            fontSize: '18px !important',
        },
        menuHeader: {
            display: 'flex',
            alignItems: 'center',
            maxWidth: '100%'
        },
        avatar: {
            backgroundColor: theme.palette.secondary.light,
            color: theme.palette.text.secondary,
            fontSize: '16px',
            width: '64px',
            height: '64px',
            marginRight: '12px'
        },
        hideMenuIcon: {
            color: theme.palette.primary.light,
            display: 'flex',
            padding: 0,
            minWidth: 0,
            justifyContent: 'flex-end',
        },
        username: {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'pre-wrap',
            maxHeight: '88px',
            overflowY: 'hidden',
            color: theme.palette.primary.light,
        }
    }),
);

function SwipeableMenu({ open, setOpen }: ISwipeableMenuProps) {
    const classes = useStyles();

    const homeContext = React.useContext(HomeContext);
    const authContext = React.useContext(AuthContext);
    const clientEnable = homeContext.influencerClientEnable;
    const userProfile = authContext.login.user.profile?.description;
    const ambevProfiles = [
        'Cliente + Influencers',
        'Cliente + Influencers + Avaliação',
        'Dashboard Influencers',
        'Cliente + Influencers Full',
        'Cliente + Influencers + Analítico'];

    // if (!clientEnable && authContext.login.user.clients && authContext.login.user.clients.length == 1) {
    //     const client = authContext.login.user.clients[0];
    //     homeContext.setInfluencerClientEnable({
    //         id: client.id,
    //         id_cliente: client.id,
    //         nome: client.name
    //     });
    // }

    const menusPorCliente: Record<string, any> = {
        'ambev 2018': MenuInfluencer,
        'ambev 2024': MenuInfluencer,
        default: MenuInfluencerICE,
    };

    const nomeCliente = clientEnable?.nome?.toLowerCase() || '';
    let MenuComponent = menusPorCliente[nomeCliente] || menusPorCliente.default;

    // Se o perfil do usuário for Ambev e não houver clientEnable, seta como ambev 2024
    if (ambevProfiles.includes(userProfile ?? '') && !clientEnable) {
        const clients = authContext.login.user.clients;
        if (clients && clients.length > 0) {
            const client = clients[0];
            homeContext.setInfluencerClientEnable({
                id: client.id,
                id_cliente: client.id,
                nome: client.name
            });
        }
    }

    // Se o perfil do usuário for um dos perfis Ambev, sempre usa MenuInfluencer
    if (userProfile && ambevProfiles.includes(userProfile)) {
        MenuComponent = MenuInfluencer;
    }

    const [menuItemOpen, setMenuItemOpen] = React.useState<MenuItems>(MenuItems.NONE);

    // Estados para dashboards disponíveis
    const [availableDashboards, setAvailableDashboards] = React.useState<IDashboard[]>([]);
    const [dashboardsLoading, setDashboardsLoading] = React.useState<boolean>(false);

    const userInitials = (fullName: string | null) => {
        if (typeof fullName != 'undefined' && fullName != null) {
            const names = fullName.split(' ');
            let initials = '';
            if (names.length == 1)
                initials = names[0].charAt(0).toUpperCase();
            else
                initials = names[0].charAt(0).toUpperCase() + names[names.length - 1].charAt(0).toUpperCase();

            return initials.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
        }
    };

    const toggleDrawer = (open: boolean, pageLocation: PageLocation | null = null) =>
        (event: React.KeyboardEvent | React.MouseEvent) => {
            if (event && event.type === 'keydown'
                && ((event as React.KeyboardEvent).key === 'Tab'
                    || (event as React.KeyboardEvent).key === 'Shift')) {
                return;
            }

            setOpen(open);
            setMenuItemOpen(MenuItems.NONE);

            if (pageLocation != null)
                homeContext.setPageLocation(pageLocation);
        };

    const handleClickHeaderMenu = (item: number) => {
        if (item === menuItemOpen)
            setMenuItemOpen(MenuItems.NONE);
        else
            setMenuItemOpen(item);
    };

    // Função para buscar dashboards disponíveis
    const fetchAvailableDashboards = React.useCallback(async () => {
        if (dashboardsLoading || availableDashboards.length > 0) return;

        try {
            setDashboardsLoading(true);
            const dashboards = await DashboardService.getAvailableDashboards();
            setAvailableDashboards(dashboards.filter(d => d.isActive));
        } catch (error) {
            console.error('Erro ao buscar dashboards disponíveis:', error);
        } finally {
            setDashboardsLoading(false);
        }
    }, [dashboardsLoading, availableDashboards.length]);

    // Carregar dashboards no carregamento da tela se o usuário tiver a funcionalidade
    React.useEffect(() => {
        if (authContext.login.user.profile?.functionalities &&
            authContext.login.user.profile.functionalities.findIndex(f => f.description === 'Acesso ao Dash') >= 0) {
            fetchAvailableDashboards();
        }
    }, [authContext.login.user.profile?.functionalities, fetchAvailableDashboards]);

    // Função para navegar para um dashboard específico
    const navigateToDashboard = (dashboard: IDashboard) => {
        homeContext.setSelectedDashboard(dashboard);
        homeContext.setPageLocation(PageLocation.DASHBOARD_GENERIC);
    };

    const getMenuHeader = () => {
        return (
            <>
                <ListItem className={classes.menuHeader}>
                    <ListItemAvatar>
                        <Avatar className={classes.avatar}>
                            {userInitials(authContext.login.user.username)}
                        </Avatar>
                    </ListItemAvatar>
                    <Grid container direction="column">
                        <Grid item>
                            <Typography variant="subtitle2" className={classes.username}>
                                {authContext.login.user.username}
                            </Typography>
                        </Grid>
                        <Grid item>
                            <Typography variant="caption" color="textSecondary">
                                {authContext.login.user.profile && authContext.login.user.profile.description}
                            </Typography>
                        </Grid>
                    </Grid>
                    <ListItemSecondaryAction>
                        <IconButton onClick={toggleDrawer(false)} className={classes.hideMenuIcon}>
                            <Icon>keyboard_arrow_left</Icon>
                        </IconButton>
                    </ListItemSecondaryAction>
                </ListItem>
                <Divider />
            </>
        );
    };

    const list = () => {
        return (
            <>
                {authContext.login.user.profile?.functionalities && (
                    <div className={classes.list} role="presentation">
                        <List
                            component="nav"
                            aria-labelledby="nested-list-subheader"
                        // className={classes.root}
                        >

                            {getMenuHeader()}

                            {authContext.login.user.profile?.functionalities.findIndex(f => f.description == 'Classificação') >= 0 && (
                                <>
                                    <MenuItemComponent
                                        key="classification-menu"
                                        principalItemTitle="Classificação"
                                        toggleDrawer={toggleDrawer}
                                        isOpen={menuItemOpen === MenuItems.CLASSIFICATION}
                                        openPrincipalItem={() => handleClickHeaderMenu(MenuItems.CLASSIFICATION)}
                                        children={[
                                            {
                                                title: 'Histórico de classificações',
                                                pageLocation: PageLocation.HISTORY,
                                                show: true
                                            },
                                            {
                                                title: 'Upload de planilha',
                                                pageLocation: PageLocation.NEWS_UPLOAD,
                                                show: true
                                            },
                                            {
                                                title: 'Classificação avulsa',
                                                pageLocation: PageLocation.DETACHED_CLASSIFICATION_INITIAL,
                                                show: true
                                            }
                                        ]} />
                                    <Divider />
                                </>
                            )}
                            {authContext.login.user.profile?.functionalities.findIndex(f =>
                                f.description == 'Revisão de relatório' || f.description == 'Correção em lote' || f.description == 'Inclusão de palavra chave') >= 0 && (
                                    <>
                                        <MenuItemComponent
                                            key="revision-menu"
                                            principalItemTitle="Revisão"
                                            toggleDrawer={toggleDrawer}
                                            isOpen={menuItemOpen === MenuItems.REVISION}
                                            openPrincipalItem={() => handleClickHeaderMenu(MenuItems.REVISION)}
                                            children={[
                                                {
                                                    title: 'Revisão de relatório',
                                                    pageLocation: PageLocation.REPORT_REVISION,
                                                    show: authContext.login.user.profile?.functionalities.findIndex(f => f.description == 'Revisão de relatório') >= 0
                                                },
                                                {
                                                    title: 'Correção em lote',
                                                    pageLocation: PageLocation.BATCH_CORRECTION,
                                                    show: authContext.login.user.profile?.functionalities.findIndex(f => f.description == 'Correção em lote') >= 0
                                                },
                                                {
                                                    title: 'Incluir palavra-chave',
                                                    pageLocation: PageLocation.KEYWORD_INCLUSION,
                                                    show: authContext.login.user.profile?.functionalities.findIndex(f => f.description == 'Inclusão de palavra chave') >= 0
                                                }
                                            ]} />
                                        <Divider />
                                    </>
                                )}
                            {authContext.login.user.profile?.functionalities.findIndex(f => f.description == 'Cadastro de jornalistas') >= 0 && (
                                <>
                                    <MenuItemComponent
                                        key="journalist-menu"
                                        principalItemTitle="Jornalista"
                                        toggleDrawer={toggleDrawer}
                                        isOpen={menuItemOpen === MenuItems.JOURNALIST}
                                        openPrincipalItem={() => handleClickHeaderMenu(MenuItems.JOURNALIST)}
                                        children={[
                                            {
                                                title: 'Incluir novo jornalista',
                                                pageLocation: PageLocation.JOURNALIST_INPUT,
                                                show: true
                                            },
                                            {
                                                title: 'Consulta de jornalistas',
                                                pageLocation: PageLocation.JOURNALIST_SEARCH,
                                                show: true
                                            }
                                        ]} />
                                    <Divider />
                                </>
                            )}
                            {authContext.login.user.profile?.functionalities.findIndex(f => f.description == 'Cadastro de veículos') >= 0 && (
                                <>
                                    <MenuItemComponent
                                        key="vehicle-menu"
                                        principalItemTitle="Veículo"
                                        toggleDrawer={toggleDrawer}
                                        isOpen={menuItemOpen === MenuItems.VEHICLE}
                                        openPrincipalItem={() => handleClickHeaderMenu(MenuItems.VEHICLE)}
                                        children={[
                                            {
                                                title: 'Incluir novo veículo',
                                                pageLocation: PageLocation.VEHICLE_INPUT,
                                                show: true
                                            },
                                            {
                                                title: 'Consulta de veículos',
                                                pageLocation: PageLocation.VEHICLE_SEARCH,
                                                show: true
                                            }
                                        ]} />
                                    <Divider />
                                </>
                            )}
                            {authContext.login.user.profile?.functionalities.findIndex(f => f.description == 'Cadastro de usuários') >= 0 && (
                                <>
                                    <MenuItemComponent
                                        key="user-menu"
                                        principalItemTitle="Usuário"
                                        toggleDrawer={toggleDrawer}
                                        isOpen={menuItemOpen === MenuItems.USER}
                                        openPrincipalItem={() => handleClickHeaderMenu(MenuItems.USER)}
                                        children={[
                                            {
                                                title: 'Incluir novo usuário',
                                                pageLocation: PageLocation.USER_INPUT,
                                                show: true
                                            },
                                            {
                                                title: 'Consulta de usuários',
                                                pageLocation: PageLocation.USER_SEARCH,
                                                show: true
                                            }
                                        ]} />
                                    <Divider />
                                </>
                            )}
                            {authContext.login.user.profile?.functionalities.findIndex(f => f.description == 'Cadastro de clientes') >= 0 && (
                                <>
                                    <MenuItemComponent
                                        key="client-menu"
                                        principalItemTitle="Cliente"
                                        toggleDrawer={toggleDrawer}
                                        isOpen={menuItemOpen === MenuItems.CLIENT}
                                        openPrincipalItem={() => handleClickHeaderMenu(MenuItems.CLIENT)}
                                        children={[
                                            {
                                                title: 'Incluir novo cliente',
                                                pageLocation: PageLocation.CLIENT_INPUT,
                                                show: true
                                            },
                                            {
                                                title: 'Consulta de clientes',
                                                pageLocation: PageLocation.CLIENT_SEARCH,
                                                show: true
                                            }
                                        ]} />
                                    <Divider />
                                </>
                            )}
                            {authContext.login.user.profile?.functionalities.findIndex(f => f.description == 'Cadastro de clipadora') >= 0 && (
                                <>
                                    <MenuItemComponent
                                        key="clipper-menu"
                                        principalItemTitle="Clipadora"
                                        toggleDrawer={toggleDrawer}
                                        isOpen={menuItemOpen === MenuItems.CLIPPER}
                                        openPrincipalItem={() => handleClickHeaderMenu(MenuItems.CLIPPER)}
                                        children={[
                                            {
                                                title: 'Incluir nova clipadora',
                                                pageLocation: PageLocation.CLIPPER_INPUT,
                                                show: true
                                            },
                                            {
                                                title: 'Consulta de clipadoras',
                                                pageLocation: PageLocation.CLIPPER_SEARCH,
                                                show: true
                                            }
                                        ]} />
                                    <Divider />
                                </>
                            )}
                            {authContext.login.user.profile?.functionalities.findIndex(f => f.description == 'Redes Sociais') >= 0 && (
                                <>
                                    <MenuItemComponent
                                        key="social-media-menu"
                                        principalItemTitle="Redes Sociais"
                                        toggleDrawer={toggleDrawer}
                                        isOpen={menuItemOpen === MenuItems.SOCIAL_MEDIA}
                                        openPrincipalItem={() => handleClickHeaderMenu(MenuItems.SOCIAL_MEDIA)}
                                        children={[
                                            {
                                                title: 'Upload de planilhas do Stilingue',
                                                pageLocation: PageLocation.SOCIAL_MEDIA_UPLOAD,
                                                show: true
                                            }
                                        ]} />
                                    <Divider />
                                </>
                            )}
                            {/* Menu influencer */}
                            {(clientEnable || ambevProfiles.includes(userProfile ?? '')) && (
                                <MenuComponent
                                    toggleDrawer={toggleDrawer}
                                />
                            )}

                            {(authContext.login.user.profile?.functionalities.findIndex(f => f.description == 'Acesso ao Dash') >= 0 && !dashboardsLoading && !(authContext.login.user.profile?.functionalities.findIndex(f => f.description == 'Acesso ao Dash Influencers') >= 0)) && (
                                <>
                                    <MenuItemComponent
                                        key="dashboard-menu"
                                        principalItemTitle="Dashboard"
                                        toggleDrawer={toggleDrawer}
                                        isOpen={availableDashboards.length > 1 ? menuItemOpen === MenuItems.DASHBOARD : false}
                                        openPrincipalItem={availableDashboards.length > 1 ? () => handleClickHeaderMenu(MenuItems.DASHBOARD) : undefined}
                                        onClickPrincipalItem={availableDashboards.length === 1 ? () => {
                                            navigateToDashboard(availableDashboards[0]);
                                            setOpen(false);
                                        } : undefined}
                                        children={availableDashboards.length > 1 ? availableDashboards.map(dashboard => ({
                                            title: dashboard.displayName,
                                            pageLocation: PageLocation.DASHBOARD_GENERIC,
                                            show: true,
                                            customAction: () => {
                                                navigateToDashboard(dashboard);
                                                setOpen(false);
                                            }
                                        })) : []} />
                                    <Divider />
                                </>
                            )}
                        </List>
                    </div >
                )
                }
            </>
        );
    };

    return (
        <div>
            <IconButton onClick={() => setOpen(true)}>
                <Icon className={classes.buttonMenu}>menu</Icon>
            </IconButton>
            <SwipeableDrawer
                open={open}
                onClose={toggleDrawer(false)}
                onOpen={toggleDrawer(true)}>

                <div className={classes.root}>
                    {list()}
                </div>

            </SwipeableDrawer>
        </div>
    );
}

export default React.memo(
    SwipeableMenu,
    (p, n) => (p.open === n.open)
);